import React, { useState, useEffect } from 'react';
import {
  ClipboardCheck,
  Users,
  FileText,
  Heart,
  CheckCircle,
  Clock,
  Bookmark,
  DollarSign,
  Calendar,
  CalendarCheck,
  ThumbsUp,
  Check,
  Pencil,
  Lock,
} from 'lucide-react';
import { UserProgram } from '@/api/entities';
import { getCurrentUser } from '@/lib/supabase';
import { supabase } from '@/lib/supabase';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useThemeSettings } from '@/contexts/ThemeContext';
import { parseLocalDate } from '@/utils/dateHelpers';
import { formatCampDisplayName } from '@/utils/campNameFormatter';

/**
 * Helper function to find child by name and return photo URL
 * Handles various name formats and matching scenarios
 */
const findChildPhoto = (childName, children = []) => {
  if (!childName || !children || children.length === 0) return null;
  
  // Normalize the input name (trim, lowercase, remove extra spaces)
  const normalize = (name) => {
    if (!name || typeof name !== 'string') return '';
    return name.trim().toLowerCase().replace(/\s+/g, ' ');
  };
  
  const normalizedInput = normalize(childName);
  if (!normalizedInput) return null;
  
  // Try exact name match
  let child = children.find(c => {
    if (!c || !c.name) return false;
    const normalizedChildName = normalize(c.name);
    return normalizedChildName === normalizedInput;
  });
  
  // If no exact match, try partial matching (first name only)
  if (!child && normalizedInput.includes(' ')) {
    const firstName = normalizedInput.split(' ')[0];
    child = children.find(c => {
      if (!c || !c.name) return false;
      const normalizedChildName = normalize(c.name);
      return normalizedChildName.split(' ')[0] === firstName || 
             normalizedChildName === firstName;
    });
  }
  
  // Also try matching just first name if input is a single word
  if (!child && !normalizedInput.includes(' ')) {
    child = children.find(c => {
      if (!c || !c.name) return false;
      const normalizedChildName = normalize(c.name);
      const childFirstName = normalizedChildName.split(' ')[0];
      return childFirstName === normalizedInput;
    });
  }
  
  // Return photo URL if found
  return child?.photo_url || null;
};

/**
 * Get initials from child name
 */
const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '??';
  const trimmed = name.trim();
  
  // Filter out generic/invalid names
  const lowerTrimmed = trimmed.toLowerCase();
  if (
    lowerTrimmed === 'child' || 
    lowerTrimmed === 'children' || 
    lowerTrimmed === 'n' ||
    lowerTrimmed === 'no name' ||
    lowerTrimmed.length === 0 ||
    trimmed.length === 1
  ) {
    return '??';
  }
  
  // Extract initials from words
  const initials = trimmed
    .split(' ')
    .filter(n => n.length > 0 && /[a-zA-Z]/.test(n[0]))
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  
  return initials || '??';
};

/**
 * Get color class for child avatar based on name
 */
const getColorClass = (name) => {
  const colors = [
    'bg-brand-500',
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-pink-500',
    'bg-orange-500',
    'bg-teal-500',
    'bg-indigo-500'
  ];
  if (!name || typeof name !== 'string' || name.trim().length === 0) return colors[0];
  const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
  return colors[index];
};


/**
 * Private Information Section Component
 * 
 * Displays user-specific information only visible in user_program mode
 * Shows registration status, children attending, and private notes
 * 
 * @param {Object} props
 * @param {Object} props.userProgram - User program data
 * @param {Object} [props.profile] - User profile data with children
 * @param {Object} [props.camp] - Camp data from camp_audit_data
 * @param {Object} [props.parentCamp] - Parent camp data (for sessions)
 * @param {Function} [props.onEdit] - Callback to trigger edit mode
 */
const PrivateInfoSection = ({ userProgram, profile = null, camp = null, parentCamp = null, onEdit }) => {
  const { theme } = useThemeSettings();
  const isDarkTheme = theme === 'dark';
  const [childSessions, setChildSessions] = useState([]);
  const [isLoadingChildSessions, setIsLoadingChildSessions] = useState(false);

  const isSession = camp?.hierarchy_level === 'session' || camp?.session || camp?.parent_place_id;
  const isParentCamp = camp && !isSession;

  useEffect(() => {
    if (!userProgram?.id || !isParentCamp) {
      setChildSessions([]);
      setIsLoadingChildSessions(false);
      return;
    }
    let isMounted = true;
    const fetchChildSessions = async () => {
      setIsLoadingChildSessions(true);
      try {
        const user = await getCurrentUser();
        if (!user?.id || !isMounted) return;

        const parentPlaceId = camp?.place_id || userProgram?.program_data?.place_id;
        const allSessions = [];

        const sessionsFromUserPrograms = await UserProgram.getSessionsForCamp(user.id, userProgram.id);
        if (sessionsFromUserPrograms?.length) allSessions.push(...sessionsFromUserPrograms);

        if (parentPlaceId && isMounted) {
          const { data: campSessions } = await supabase
            .from('camp_audit_data')
            .select('id')
            .eq('parent_place_id', parentPlaceId)
            .eq('hierarchy_level', 'session');
          const sessionIds = (campSessions || []).map((s) => s.id).filter(Boolean);
          if (sessionIds.length > 0) {
            const { data: ucsRows } = await supabase
              .from('user_camp_sessions')
              .select('*')
              .eq('user_id', user.id)
              .in('camp_id', sessionIds)
              .order('user_camp_start_date', { ascending: true, nullsFirst: false });
            if (ucsRows?.length && isMounted) {
              ucsRows.forEach((ucs) => {
                const existing = allSessions.find(
                  (s) => (s.from_user_camp_sessions && s.id === ucs.id) || (s.program_data?.session === ucs.session_name && String(s.program_data?.session_year) === String(ucs.session_year))
                );
                if (!existing) {
                  allSessions.push({
                    id: ucs.id,
                    session: ucs.session_name,
                    session_name: ucs.session_name,
                    session_year: ucs.session_year,
                    status: ucs.status,
                    user_camp_start_date: ucs.user_camp_start_date,
                    user_camp_end_date: ucs.user_camp_end_date,
                    program_data: {
                      session: ucs.session_name,
                      session_name: ucs.session_name,
                      session_year: ucs.session_year,
                      user_camp_start_date: ucs.user_camp_start_date,
                      user_camp_end_date: ucs.user_camp_end_date,
                    },
                    child_names: (() => {
                      const ids = Array.isArray(ucs.children) ? ucs.children : (ucs.children ? [ucs.children] : []);
                      return ids.map((id) => {
                        const child = profile?.children?.find((c) => c.id === id || String(c.id) === String(id));
                        return child?.name || id;
                      }).filter(Boolean);
                    })(),
                    from_user_camp_sessions: true,
                  });
                }
              });
            }
          }
        }

        if (!isMounted) return;
        const bookedOrConsidering = allSessions.filter((s) => {
          if (s.from_user_camp_sessions) return true;
          const status = (Array.isArray(s.status) ? s.status[0] : s.status) || s.program_data?.selected_statuses?.[0];
          const v = String(status || '').toLowerCase().trim();
          return ['booked', 'confirmed', 'applied', 'registered', 'enrolled', 'considering'].includes(v);
        });
        setChildSessions(bookedOrConsidering);
      } catch (err) {
        console.warn('Error loading child sessions for Private tab:', err);
        if (isMounted) setChildSessions([]);
      } finally {
        if (isMounted) setIsLoadingChildSessions(false);
      }
    };
    fetchChildSessions();
    return () => { isMounted = false; };
  }, [userProgram?.id, userProgram?.program_data?.place_id, isParentCamp, camp?.place_id, profile?.children]);

  if (!userProgram) {
    return null;
  }

  // StatusBadge component - matches EditProgramForm styling for consistency
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      favorite: {
        bg: 'bg-pink-500',
        border: 'border-pink-600',
        text: 'text-white',
        icon: Heart,
        label: 'Favorite',
        ring: 'ring-pink-500',
      },
      booked: {
        bg: 'bg-purple-500',
        border: 'border-purple-600',
        text: 'text-white',
        icon: Check,
        label: 'Booked',
        ring: 'ring-purple-500',
      },
      considering: {
        bg: 'bg-yellow-500',
        border: 'border-yellow-600',
        text: 'text-white',
        icon: Clock,
        label: 'Considering',
        ring: 'ring-yellow-500',
      },
      attended: {
        bg: 'bg-purple-500',
        border: 'border-purple-600',
        text: 'text-white',
        icon: CalendarCheck,
        label: 'Attended',
        ring: 'ring-purple-500',
      },
      recommended: {
        bg: 'bg-indigo-500',
        border: 'border-indigo-600',
        text: 'text-white',
        icon: ThumbsUp,
        label: 'Recommended',
        ring: 'ring-indigo-500',
      },
    };

    const config = statusConfig[status];
    if (!config) return null;

    const Icon = config.icon;

    return (
      <div
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 font-medium text-sm shadow-md ring-2 ring-offset-1',
          config.bg,
          config.border,
          config.text,
          config.ring
        )}
      >
        <Icon className={cn(
          'w-4 h-4',
          status === 'booked' && 'stroke-[3]'
        )} />
        <span>{config.label}</span>
      </div>
    );
  };

  // Build a canonical, de-duplicated list of statuses to display
  const getAllStatuses = () => {
    const raw = [];

    // Collect from userProgram.status
    if (Array.isArray(userProgram.status)) {
      raw.push(...userProgram.status);
    } else if (userProgram.status) {
      raw.push(userProgram.status);
    }

    // Collect from program_data.selected_statuses
    const selected = userProgram.program_data?.selected_statuses;
    if (Array.isArray(selected)) {
      raw.push(...selected);
    } else if (selected) {
      raw.push(selected);
    }

    const mapped = [];
    raw.forEach((s) => {
      if (!s) return;
      const v = String(s).toLowerCase().trim();
      let canonical = null;
      if (['booked', 'confirmed', 'applied', 'registered', 'enrolled'].includes(v)) {
        canonical = 'booked';
      } else if (v === 'considering') {
        canonical = 'considering';
      } else if (v === 'favorite' || v.startsWith('favorite')) {
        canonical = 'favorite';
      } else if (v === 'attended' || v === 'completed') {
        canonical = 'attended';
      } else if (v === 'saved') {
        canonical = 'saved';
      }
      if (canonical) {
        mapped.push(canonical);
      }
    });

    const unique = Array.from(new Set(mapped));
    const order = ['booked', 'considering', 'favorite', 'attended', 'recommended', 'saved'];
    unique.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    // Filter out 'saved' status - don't display it
    const filtered = unique.filter(s => s !== 'saved');
    return filtered.length > 0 ? filtered : [];
  };

  const allStatuses = getAllStatuses();
  
  // Get formatted camp name with program/session information
  const getFormattedCampName = () => {
    // If we have camp data from camp_audit_data, use it (more reliable for sessions)
    if (camp) {
      // For sessions, show "Parent Name - Session Name"
      if (isSession && camp.session) {
        const parentName = parentCamp?.name || camp.name;
        return `${parentName} - ${camp.session}`;
      }
      // Use formatCampDisplayName for other cases
      return formatCampDisplayName(camp);
    }
    
    // Fallback to userProgram data
    const programData = userProgram.program_data || {};
    return formatCampDisplayName({
      name: programData.name || userProgram.name,
      program: programData.program,
      session: programData.session,
      session_year: programData.session_year,
      session_expires_at: programData.session_expires_at,
    });
  };

  const formattedCampName = getFormattedCampName();
  
  // Only show Program/Session section for sessions or when we have program/session data
  const shouldShowProgramSessionSection = isSession || formattedCampName.includes(' - ');

  // Helper function to format date range
  const formatDateRange = (start, end) => {
    if (!start || !end) return null;
    
    try {
      // Use parseLocalDate to prevent timezone conversion issues
      const startDate = parseLocalDate(start);
      const endDate = parseLocalDate(end);
      
      // Check if dates are valid
      if (!startDate || !endDate) {
        return null;
      }
      
      return `${startDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      })} - ${endDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      })}`;
    } catch (error) {
      return null;
    }
  };

  // Get date range - try multiple sources
  const getDateRange = () => {
    const programData = userProgram.program_data || {};
    
    // Try user_camp_start_date and user_camp_end_date first
    if (programData.user_camp_start_date && programData.user_camp_end_date) {
      return formatDateRange(programData.user_camp_start_date, programData.user_camp_end_date);
    }
    // Try camp_start_date and camp_end_date
    if (programData.camp_start_date && programData.camp_end_date) {
      return formatDateRange(programData.camp_start_date, programData.camp_end_date);
    }
    // Try start_date and end_date
    if (programData.start_date && programData.end_date) {
      return formatDateRange(programData.start_date, programData.end_date);
    }
    // Try campStartDate and campEndDate
    if (programData.campStartDate && programData.campEndDate) {
      return formatDateRange(programData.campStartDate, programData.campEndDate);
    }
    return null;
  };

  const dateRange = getDateRange();

  // Check if we should show empty state (no meaningful data to display).
  // We always show the Additional Notes section in view mode, so we always have at least that content — never show full-page empty state.
  const hasStatus = allStatuses.length > 0;
  const hasDates = !!dateRange;
  const hasChildren = userProgram.child_names && userProgram.child_names.length > 0;
  const hasNotes = userProgram.program_data?.notes && userProgram.program_data.notes.trim().length > 0;
  const hasProgramSessionInfo = shouldShowProgramSessionSection && formattedCampName;
  const hasAnyData = hasStatus || hasDates || hasChildren || hasNotes || hasProgramSessionInfo || childSessions.length > 0 || isLoadingChildSessions || true;

  // Empty state - show when there's no meaningful data (and not loading child sessions)
  if (!hasAnyData) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Ready to plan?</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Tap "Edit" to add sessions to your summer timeline and track which children are attending.
          </p>
          <Button
            onClick={() => {
              if (onEdit) {
                onEdit();
              } else if (window.location.pathname.includes('/program/')) {
                const url = new URL(window.location.href);
                url.searchParams.set('edit', 'true');
                window.location.href = url.toString();
              }
            }}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            Edit
          </Button>
        </div>
      </div>
    );
  }

  const getSessionDisplayName = (session) => {
    const pd = session.program_data || {};
    const sessionName = session.session || session.session_name || pd.session || pd.session_name || '';
    const parentName = parentCamp?.name || camp?.name || pd.name || '';
    return sessionName ? `${parentName || 'Session'} - ${sessionName}` : (parentName || 'Session');
  };

  const getSessionStatus = (session) => {
    const status = (Array.isArray(session.status) ? session.status[0] : session.status) || session.program_data?.selected_statuses?.[0];
    const v = String(status || '').toLowerCase().trim();
    if (['booked', 'confirmed', 'applied', 'registered', 'enrolled'].includes(v)) return 'booked';
    if (v === 'considering') return 'considering';
    if (session.from_user_camp_sessions) return 'considering';
    return 'considering';
  };

  const getSessionDateRange = (session) => {
    const pd = session.program_data || {};
    const start = pd.user_camp_start_date || pd.camp_start_date || pd.start_date || pd.campStartDate || session.user_camp_start_date || session.start_date;
    const end = pd.user_camp_end_date || pd.camp_end_date || pd.end_date || pd.campEndDate || session.user_camp_end_date || session.end_date;
    return formatDateRange(start, end);
  };

  return (
    <>
      {/* Private Content */}
      <div className="max-w-4xl mx-auto px-6 pb-8 space-y-6 bg-purple-50/30 rounded-lg">
        {/* Child Sessions on Plan - When viewing parent camp */}
        {isParentCamp && (childSessions.length > 0 || isLoadingChildSessions) && (
          <section>
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
              <CalendarCheck className="w-4 h-4 text-brand-600 flex-shrink-0" />
              <h3 className="text-sm font-semibold text-gray-900">
                Sessions on Your Plan
              </h3>
            </div>
            {isLoadingChildSessions ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-4 bg-white rounded-xl border border-gray-200 animate-pulse">
                    <div className="h-4 w-3/4 bg-gray-200 rounded mb-2" />
                    <div className="h-3 w-1/2 bg-gray-100 rounded" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {childSessions.map((session) => {
                  const sessionName = getSessionDisplayName(session);
                  const status = getSessionStatus(session);
                  const dateRangeStr = getSessionDateRange(session);
                  const rawChildNames = session.child_names || session.program_data?.child_names;
                  const childNames = Array.isArray(rawChildNames)
                    ? rawChildNames
                    : (typeof rawChildNames === 'string' ? rawChildNames.split(',').map(n => n.trim()).filter(Boolean) : []);
                  return (
                    <div
                      key={session.id}
                      className="p-4 bg-white rounded-xl border border-gray-200"
                      style={{ borderLeft: '4px solid #7c4dff' }}
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 text-sm">{sessionName}</div>
                          {dateRangeStr && (
                            <div className="text-xs text-gray-600 mt-1">{dateRangeStr}</div>
                          )}
                          {childNames.length > 0 && (
                            <div className="flex items-center gap-1 mt-2 flex-wrap">
                              <Users className="w-3.5 h-3.5 text-gray-500" />
                              <span className="text-xs text-gray-600">{childNames.join(', ')}</span>
                            </div>
                          )}
                        </div>
                        <StatusBadge status={status} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Program/Session Information - Only show for sessions */}
        {shouldShowProgramSessionSection && formattedCampName && (
          <section>
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
              <FileText className="w-4 h-4 text-brand-600 flex-shrink-0" />
              <h3 className="text-sm font-semibold text-gray-900">
                Program/Session Information
              </h3>
            </div>
            <div className="p-4 bg-white rounded-xl border border-gray-200" style={{
              borderLeft: '4px solid #7c4dff'
            }}>
              <div className="text-sm font-medium text-gray-900">
                {formattedCampName}
              </div>
            </div>
          </section>
        )}

        {/* Status */}
        {allStatuses.length > 0 && (
          <section>
            <div className="flex items-center gap-3 mb-3 pb-2 border-b border-gray-200">
              <ClipboardCheck className="w-4 h-4 text-brand-600 flex-shrink-0" />
              <h3 className="text-sm font-semibold text-gray-900">
                Status
              </h3>
              <div className="flex flex-wrap gap-2">
                {allStatuses.map((s) => (
                  <StatusBadge key={s} status={s} />
                ))}
              </div>
            </div>
            {userProgram.program_data?.booking_confirmation && (
              <div className="mt-3 text-xs text-gray-600">
                Confirmation #{userProgram.program_data.booking_confirmation}
              </div>
            )}
          </section>
        )}

        {/* Camp Dates */}
        {dateRange && (
          <section>
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
              <Calendar className="w-4 h-4 text-brand-600 flex-shrink-0" />
              <h3 className="text-sm font-semibold text-gray-900">
                Camp Dates
              </h3>
            </div>
            <div className="p-4 bg-white rounded-xl border border-gray-200">
              <div className="text-sm font-medium text-gray-900">
                {dateRange}
              </div>
            </div>
          </section>
        )}

        {/* Children Attending */}
        {userProgram.child_names && userProgram.child_names.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
              <Users className="w-4 h-4 text-brand-600 flex-shrink-0" />
              <h3 className="text-sm font-semibold text-gray-900">
                Children Attending
              </h3>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {userProgram.child_names.map((childName) => {
                const child =
                  profile?.children?.find((c) => c.name === childName) || null;
                
                // Get photo URL using helper function
                const photoUrl = findChildPhoto(childName, profile?.children || []);
                
                // Get individual child pricing
                const childrenPricing = userProgram.program_data?.children_pricing || {};
                const childPrice = childrenPricing[childName] || childrenPricing[childName.toLowerCase()];

                return (
                  <div
                    key={childName}
                    className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 hover:border-purple-200 transition"
                  >
                    <div className="relative flex-shrink-0">
                      {photoUrl ? (
                        <div 
                          className="w-12 h-12 p-0.5 rounded-full flex items-center justify-center"
                          style={{
                            background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.75) 0%, rgba(13, 148, 136, 0.75) 25%, rgba(104, 94, 255, 0.75) 50%, rgba(236, 72, 153, 0.75) 75%, rgba(245, 158, 11, 0.75) 100%)'
                          }}
                        >
                          <img
                            src={photoUrl}
                            alt={childName}
                            className="w-full h-full rounded-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const parent = e.target.parentElement;
                              if (parent && !parent.querySelector('.fallback-initials')) {
                                const fallback = document.createElement('div');
                                fallback.className = `w-full h-full rounded-full ${isDarkTheme ? 'bg-slate-900' : 'bg-white'} flex items-center justify-center fallback-initials`;
                                fallback.innerHTML = `<span class="text-sm font-semibold ${isDarkTheme ? 'text-slate-300' : 'text-slate-700'}">${getInitials(childName)}</span>`;
                                parent.appendChild(fallback);
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <div 
                          className="w-12 h-12 p-0.5 rounded-full flex items-center justify-center"
                          style={{
                            background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.75) 0%, rgba(13, 148, 136, 0.75) 25%, rgba(104, 94, 255, 0.75) 50%, rgba(236, 72, 153, 0.75) 75%, rgba(245, 158, 11, 0.75) 100%)'
                          }}
                        >
                          <div className={`w-full h-full rounded-full ${isDarkTheme ? 'bg-slate-900' : 'bg-white'} flex items-center justify-center`}>
                            <span className={`text-sm font-semibold ${isDarkTheme ? 'text-slate-300' : 'text-slate-700'}`}>
                              {getInitials(childName)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm truncate">
                        {childName}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {child?.age && (
                          <div className="text-sm text-gray-600">Age {child.age}</div>
                        )}
                        {childPrice && (
                          <div className="text-sm font-medium text-brand-600">
                            ${childPrice}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Total Price Calculation */}
            {(() => {
              // Ensure child_names is an array
              const childNames = Array.isArray(userProgram.child_names) 
                ? userProgram.child_names 
                : (typeof userProgram.child_names === 'string' 
                  ? userProgram.child_names.split(',').map(name => name.trim()).filter(Boolean)
                  : []);
              
              if (childNames.length === 0) return null;
              
              const childrenPricing = userProgram.program_data?.children_pricing || {};
              const totalPrice = childNames.reduce((sum, childName) => {
                const price = childrenPricing[childName] || childrenPricing[childName.toLowerCase()] || 0;
                return sum + (typeof price === 'number' ? price : parseFloat(price) || 0);
              }, 0);
              
              // Also check if there's a direct total_price field
              const directTotal = userProgram.program_data?.total_price;
              const finalTotal = directTotal || totalPrice;
              
              if (finalTotal > 0) {
                return (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        <span className="text-sm font-medium text-gray-900">Total Price</span>
                      </div>
                      <span className="text-2xl font-bold text-green-700">
                        ${finalTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </section>
        )}

        {/* Additional Notes - always visible with Edit + pencil */}
        <section>
          <div className="flex items-center justify-between gap-2 mb-3 pb-2 border-b border-gray-200">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-4 h-4 text-brand-600 flex-shrink-0" />
              <h3 className="text-sm font-semibold text-gray-900">Additional Notes</h3>
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                <Lock className="w-3 h-3" />
                Private
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit?.()}
              className="flex-shrink-0 border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <Pencil className="w-4 h-4 mr-1" />
              Edit
            </Button>
          </div>
          <div className="p-4 bg-white rounded-xl border border-gray-200">
            {userProgram.program_data?.notes?.trim() ? (
              <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {userProgram.program_data.notes}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">
                Add notes about this program (only visible to you).
              </p>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default PrivateInfoSection;
